import React, {useState} from 'react';

import {useI18n} from '@udemy/i18n';
import {Icon} from '@udemy/icons';
import {SpotIllustration} from '@udemy/illustrations';
import {Button} from '@udemy/react-core-components';
import {Modal} from '@udemy/react-dialog-components';
import {Checkbox, ContentFeedback, ContentFeedbackState} from '@udemy/react-form-components';
import {TakeOverWizard} from '@udemy/react-structure-components';
import {useUDData} from '@udemy/ud-data';

import styles from '../pop-quiz-modal.module.less';
import {renderUdemyLogo} from './render-udemy-logo';
import {PopQuizFeedbackRating, QuizCandidate} from './types';

interface PopQuizResultsPhaseProps {
    isOpen: boolean;
    quizData: QuizCandidate;
    correctCount: number;
    totalQuestions: number;
    stopShowing: boolean;
    setStopShowing: (v: boolean) => void;
    onExit: () => void;
    onContinueLearning: () => void;
    onFeedback: (rating: PopQuizFeedbackRating) => void;
    popQuizV2ExperienceEnabled: boolean;
}

export function PopQuizResultsPhase({
    isOpen,
    quizData,
    correctCount,
    totalQuestions,
    stopShowing,
    setStopShowing,
    onExit,
    onContinueLearning,
    onFeedback,
    popQuizV2ExperienceEnabled,
}: PopQuizResultsPhaseProps) {
    const {gettext, interpolate} = useI18n();
    const {Config} = useUDData();
    // Local only — feedback is one-shot per quiz completion; there is nothing to persist client-side.
    const [feedbackValue, setFeedbackValue] = useState<ContentFeedbackState>('NONE');
    const [feedbackSubmitted, setFeedbackSubmitted] = useState(false);

    const handleFeedback = (rating: ContentFeedbackState) => {
        if (feedbackSubmitted || rating === 'NONE') {
            return;
        }
        setFeedbackValue(rating);
        setFeedbackSubmitted(true);
        onFeedback(rating);
    };

    const pct = totalQuestions > 0 ? correctCount / totalQuestions : 0;

    // Celebration tiers, compared against the quiz's last real attempt
    const {lastAttempt} = quizData;
    const hasImproved =
        popQuizV2ExperienceEnabled && !!lastAttempt && pct > lastAttempt.percentCorrect;
    // pct > lastAttempt.percentCorrect is already implied by hasImproved, so pct === 1 here
    // necessarily means the previous attempt wasn't perfect — i.e. this is the first time.
    const isFirstPerfectScore = hasImproved && pct === 1;
    const improvementDeltaPct = hasImproved
        ? Math.round((pct - (lastAttempt?.percentCorrect ?? 0)) * 100)
        : 0;

    const {resultTitle, resultIconName, resultBgClass} = (() => {
        if (pct === 1) {
            return {
                resultTitle: gettext('Nailed it'),
                resultIconName: 'goal-crushing' as const,
                resultBgClass: styles['results-bg-perfect'],
            };
        }
        if (pct >= 0.8) {
            return {
                resultTitle: gettext('Solid'),
                resultIconName: 'thank-you' as const,
                resultBgClass: styles['results-bg-solid'],
            };
        }
        if (pct >= 0.5) {
            return {
                resultTitle: gettext('Not bad'),
                resultIconName: 'personal-plan' as const,
                resultBgClass: styles['results-bg-notbad'],
            };
        }
        return {
            resultTitle: gettext('Tough one'),
            resultIconName: 'error' as const,
            resultBgClass: styles['results-bg-toughone'],
        };
    })();

    // Swap in a more celebratory illustration/background for the first-perfect-score tier —
    // title stays driven by the plain score band above; only the artwork and background change.
    const displayIconName = isFirstPerfectScore
        ? ('dance' as const)
        : hasImproved
          ? ('reach-your-goals' as const)
          : resultIconName;
    const displayBgClass = isFirstPerfectScore ? styles['results-bg-celebration'] : resultBgClass;

    return (
        <Modal
            isOpen={isOpen}
            onClose={onExit}
            title=""
            renderTitle={() => [
                'results-modal-label',
                <span key="t" id="results-modal-label" className="ud-sr-only">
                    {gettext('Results')}
                </span>,
            ]}
            className={styles['quiz-modal']}
        >
            <TakeOverWizard
                fullScreenHeight={false}
                headerProps={{
                    renderLogo: renderUdemyLogo(Config.brand.logo_url_2x),
                    centerChildren: gettext('Results'),
                    rightChildren: (
                        <Button udStyle="ghost" size="medium" onClick={onExit}>
                            {gettext('Exit')}
                        </Button>
                    ),
                }}
                footerProps={{
                    leftChildren: (
                        <Checkbox
                            checked={stopShowing}
                            onChange={(e) => setStopShowing(e.target.checked)}
                        >
                            {gettext('Stop showing me this')}
                        </Checkbox>
                    ),
                    rightChildren: (
                        <div className={styles['footer-actions']}>
                            <Button udStyle="ghost" size="medium" onClick={onExit}>
                                {gettext('Not now')}
                            </Button>
                            <Button udStyle="primary" size="medium" onClick={onContinueLearning}>
                                {gettext('Keep learning')}
                            </Button>
                        </div>
                    ),
                }}
            >
                <div className={styles['results-content']}>
                    <div className={`${styles['results-container']} ${displayBgClass}`}>
                        <p className={`ud-heading-xl ${styles['results-title']}`}>
                            {isFirstPerfectScore ? (
                                <span className={styles['results-title-celebration']}>
                                    <Icon glyph="medal" label={false} size="large" />
                                    {gettext('Perfect Score! 100%')}
                                    <Icon glyph="medal" label={false} size="large" />
                                </span>
                            ) : (
                                resultTitle
                            )}
                        </p>
                        <div className={styles['results-icon']}>
                            <SpotIllustration
                                name={displayIconName}
                                width={isFirstPerfectScore ? 140 : 96}
                                height={isFirstPerfectScore ? 140 : 96}
                                alt=""
                            />
                        </div>
                        <div className={styles['score-card']}>
                            <p className="ud-heading-xl">
                                {interpolate(
                                    gettext('%(correct)s / %(total)s'),
                                    {correct: correctCount, total: totalQuestions},
                                    true,
                                )}
                            </p>
                            <p className={`ud-text-md ${styles['score-label']}`}>
                                {gettext('Questions answered correctly')}
                            </p>
                            {isFirstPerfectScore ? (
                                <p
                                    className={`ud-text-sm ${styles['improvement-badge']} ${styles['improvement-badge-celebration']}`}
                                >
                                    <Icon glyph="trophy" label={false} size="medium" />
                                    {gettext("First time you've nailed this one!")}
                                </p>
                            ) : (
                                hasImproved && (
                                    <p className={`ud-text-sm ${styles['improvement-badge']}`}>
                                        <Icon glyph="trophy" label={false} size="xsmall" />
                                        {interpolate(
                                            gettext('+%(delta)s% vs last attempt'),
                                            {delta: improvementDeltaPct},
                                            true,
                                        )}
                                    </p>
                                )
                            )}
                        </div>
                    </div>
                    <p className={`ud-text-md ${styles['resume-prompt']}`}>
                        {quizData.source === 'refresher'
                            ? interpolate(
                                  gettext(
                                      "It's been a while since you were in %(courseTitle)s — you're %(pct)s% through. Pick up where you left off?",
                                  ),
                                  {
                                      pct: quizData.courseProgress,
                                      courseTitle: quizData.courseTitle,
                                  },
                                  true,
                              )
                            : interpolate(
                                  gettext(
                                      "You're %(pct)s% through %(courseTitle)s. Ready to keep going?",
                                  ),
                                  {
                                      pct: quizData.courseProgress,
                                      courseTitle: quizData.courseTitle,
                                  },
                                  true,
                              )}
                    </p>
                    <div className={styles['feedback-row']}>
                        {feedbackSubmitted ? (
                            <p className="ud-text-sm">{gettext('Thanks for your feedback!')}</p>
                        ) : (
                            <>
                                <p className="ud-text-sm">{gettext('Enjoying this feature?')}</p>
                                <ContentFeedback
                                    feedbackValue={feedbackValue}
                                    formLegend={gettext('Was this quiz helpful?')}
                                    onPositiveClick={handleFeedback}
                                    onNegativeClick={handleFeedback}
                                />
                            </>
                        )}
                    </div>
                </div>
            </TakeOverWizard>
        </Modal>
    );
}
